import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { openScreen, openSidebar } from '@slices/uiSlice'
import { useRoundInfo } from '@hooks/queries'
import { useCopyToClipboard } from '@hooks/useCopyToClipboard'
import { verifyRound } from '@core/provablyFair/crashFairness'
import { soundManager } from '@core/audio/sounds/soundManager'

// Provably-fair detail for one crash-history chip.
//
// Rendered through a portal because the chip row is an `overflow: auto` scroller
// that would clip an in-flow panel, and because a fixed-position panel must not
// sit inside an ancestor that establishes its own containing block (the chip
// entrance animation temporarily sets `will-change` on a chip, which would).
//
// The tick is computed from arithmetic this browser performed via
// verifyRound() — never from the API's success flag.

const PANEL_WIDTH = 268
const VIEWPORT_MARGIN = 8
const ANCHOR_GAP = 10

// Middle-ellipsis: keeps both ends visible, which is what a player compares
// against their own computation. The full value is always what gets copied.
const truncate = (value, edge = 10) =>
  !value ? null : value.length <= edge * 2 + 1 ? value : `${value.slice(0, edge)}…${value.slice(-edge)}`

const STATUS_ICON = { pass: '✓', fail: '✕', pending: '•', interrupted: '!' }

// Provenance goes in the row LABEL rather than a badge: the card is a fixed
// 268px and every existing row is one label + one value, so a new element would
// mean new CSS and a new height to re-measure. "Seed 1 (you)" carries the same
// information inside the layout that is already there.
const SEED_ORIGIN = { PLAYER: 'player', SERVER_FALLBACK: 'server' }

const seedLabel = (entry, playerId) => {
  if (entry.source === 'SERVER_FALLBACK') return `Seed ${entry.slot} (server)`
  if (playerId && entry.playerId === playerId) return `Seed ${entry.slot} (you)`
  return `Seed ${entry.slot} (${SEED_ORIGIN[entry.source] ?? 'player'})`
}

const ValueRow = ({ label, value, hint, copy, isCopied }) => (
  <div className="crash-fairness__row">
    <span className="crash-fairness__row-label">{label}</span>

    {value ? (
      <button
        type="button"
        className="crash-fairness__row-value"
        onClick={() => copy(value, label)}
        title={`${value}\n\nClick to copy`}
        aria-label={`Copy ${label}`}
      >
        <code>{truncate(value)}</code>
        <span className="crash-fairness__row-copy">{isCopied(label) ? 'copied' : 'copy'}</span>
      </button>
    ) : (
      <span className="crash-fairness__row-hint">{hint ?? '—'}</span>
    )}
  </div>
)

const CrashRoundFairness = ({
  anchorEl,
  roundId,
  variant = 'normal',
  pending = null,
  onRequestClose,
  onMouseIn,
  onMouseOut,
}) => {
  const panelRef = useRef(null)
  const dispatch = useDispatch()
  // Only used to mark which of the three seeds is the viewer's own.
  const playerId = useSelector((state) => state.session.details.playerId)
  const [position, setPosition] = useState(null)
  const [verification, setVerification] = useState(null)

  const { copy, isCopied } = useCopyToClipboard()

  // A pending round has no published seed and no outcome, so it is NOT fetched —
  // querying it would return null forever and the poll below would never settle.
  // Everything the commitment view shows arrives on the live round events.
  const { data: round, isLoading } = useRoundInfo(
    { roundId: pending ? undefined : roundId },
    { refetchInterval: (query) => (query.state.data ? false : 1500) }
  )

  // Hand the round off to the Provably Fair screen, which does the full
  // verification. Reuses the existing side-menu navigation and the screen's own
  // components — no verification logic is duplicated here.
  const handleVerify = () => {
    soundManager.play('CLICK')

    // openSidebar FIRST, and it must come first: the menu options dispatch
    // openScreen from inside an already-open panel, but this button fires from the
    // game area where the panel is closed — openScreen alone only pushes history,
    // leaving the panel translated off-screen. openSidebar also CLEARS history,
    // so doing it second would discard the screen we just pushed.
    dispatch(openSidebar())
    dispatch(
      openScreen({
        key: 'provablyFair',
        title: 'Provably Fair',
        screen: 'PROVABLY_FAIR',
        params: { roundId },
      })
    )
    onRequestClose()
  }

  // Keep the panel pinned to its chip: on mount, whenever the panel's own height
  // changes, and while the row scrolls or the window resizes.
  //
  // Deliberately ONE effect. Splitting the initial placement from a re-measure
  // let the two disagree — `top` flipped above the chip while `placement` still
  // read "below" — so both are always computed together from the real height.
  //
  // A ResizeObserver drives the re-measure rather than a dependency list. The
  // panel grows as content lands (seed rows, then the verdict once local
  // verification finishes), and when flipped ABOVE its top edge depends on that
  // height — enumerating every state that affects it is how the panel ended up
  // overlapping its own chip.
  useLayoutEffect(() => {
    if (!anchorEl) return undefined

    let frame = null

    const place = () => {
      if (!anchorEl.isConnected) {
        onRequestClose()
        return
      }

      const anchor = anchorEl.getBoundingClientRect()
      const panelHeight = panelRef.current?.offsetHeight ?? 0

      // Clamp horizontally so the panel never leaves the viewport.
      const maxLeft = window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN
      const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left + anchor.width / 2 - PANEL_WIDTH / 2, maxLeft))

      // Prefer below the chip; flip above when there is not enough room.
      const below = anchor.bottom + ANCHOR_GAP
      const fitsBelow = below + panelHeight + VIEWPORT_MARGIN <= window.innerHeight
      const top = fitsBelow ? below : Math.max(VIEWPORT_MARGIN, anchor.top - ANCHOR_GAP - panelHeight)
      const placement = fitsBelow ? 'below' : 'above'

      setPosition((prev) =>
        prev && prev.top === top && prev.left === left && prev.placement === placement
          ? prev
          : { top, left, placement }
      )
    }

    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    }

    place()

    const observer = new ResizeObserver(schedule)
    if (panelRef.current) observer.observe(panelRef.current)

    // `true` captures scrolls on the chip row itself, not just the window.
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
    }
  }, [anchorEl, onRequestClose])

  // Verify locally whenever a payload arrives.
  useEffect(() => {
    const payload = round?.raw
    if (!payload) return undefined

    let cancelled = false
    verifyRound(payload).then((result) => {
      if (!cancelled) setVerification({ roundId: payload.roundId, result })
    })

    return () => {
      cancelled = true
    }
  }, [round])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onRequestClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onRequestClose])

  // Native pointerover/pointerout rather than React's onPointerEnter/Leave.
  // React synthesises enter/leave from the React tree, and this panel lives in a
  // portal whose React parent is the chip row — so moving the mouse from a chip
  // into the panel did not produce an enter here, and the pending close timer
  // fired underneath the pointer. That made copying with a mouse impossible.
  useEffect(() => {
    const el = panelRef.current
    if (!el) return undefined

    const handleOver = (event) => {
      if (event.pointerType === 'mouse') onMouseIn?.()
    }
    const handleOut = (event) => {
      // pointerout also fires when moving BETWEEN children; only a move that
      // actually leaves the panel should start the close.
      if (event.pointerType === 'mouse' && !el.contains(event.relatedTarget)) onMouseOut?.()
    }

    el.addEventListener('pointerover', handleOver)
    el.addEventListener('pointerout', handleOut)

    return () => {
      el.removeEventListener('pointerover', handleOver)
      el.removeEventListener('pointerout', handleOut)
    }
  }, [onMouseIn, onMouseOut])

  const active = round && verification?.roundId === round.roundId ? verification.result : null

  const verdict = !active
    ? null
    : active.error
      ? { status: 'fail', text: active.error }
      : !active.verified
        ? { status: 'fail', text: 'Verification failed' }
        : active.crashPoint.status === 'interrupted'
          ? { status: 'interrupted', text: 'Interrupted — seed verified, settled early' }
          : active.commitment.status === 'pending'
            ? { status: 'pending', text: 'Crash point verified in your browser' }
            : { status: 'pass', text: 'Provably fair' }

  return createPortal(
    <div
      ref={panelRef}
      // The variant modifier selects which theme token set the card reads: the
      // layout controller applies ONLY `--standard-mini-*` in mini mode and only
      // `--standard-normal-*` in normal mode, so a single token set would leave
      // the card on hardcoded fallbacks in one of the two layouts.
      className={`crash-fairness crash-fairness--${position?.placement ?? 'below'} crash-fairness--${variant}`}
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        width: PANEL_WIDTH,
        visibility: position ? 'visible' : 'hidden',
      }}
      role="dialog"
      aria-label="Provably fair details"
      // Identifies which round this card belongs to. Two rows of chips can each
      // have a card open at once (one hovered, one tapped), so the round is part
      // of the card's identity rather than something inferred from DOM order.
      data-round-id={roundId}
    >
      {/* ── Commitment-only view for the round now in play ──────────────────
          Shows what is already public — the round id and the seed hash the
          server committed to before betting opened — and nothing else. There is
          deliberately no server seed and no outcome here: neither exists in this
          component's data, because the API does not publish them until the round
          has ended. */}
      {pending && (
        <>
          <div className="crash-fairness__header">
            <span className="crash-fairness__round">Current round</span>
            <span className="crash-fairness__crash crash-fairness__crash--pending">?</span>
          </div>

          <div className="crash-fairness__rows">
            <ValueRow label="Round ID" value={pending.roundId} copy={copy} isCopied={isCopied} />
            <ValueRow
              label="Seed Hash"
              value={pending.serverSeedHash}
              copy={copy}
              isCopied={isCopied}
            />
            <ValueRow
              label="Server Seed"
              value={null}
              hint="hidden until round ends"
              copy={copy}
              isCopied={isCopied}
            />
          </div>

          <div className="crash-fairness__verdict is-pending">
            <span className="crash-fairness__verdict-icon">•</span>
            Committed before betting — outcome not yet determined
          </div>
        </>
      )}

      {!pending && isLoading && !round && <div className="crash-fairness__pending">Loading…</div>}

      {!pending && !isLoading && !round && (
        <div className="crash-fairness__pending">
          Seed is published once the round is settled.
        </div>
      )}

      {round && (
        <>
          <div className="crash-fairness__header">
            {/* "Seed round", not "Round #": this is the round's position within
                its server seed (1..quota), which restarts on every rotation —
                labelling it as a round number made hundreds of rounds look like
                they shared one identity. The round's identity is its Round ID. */}
            <span className="crash-fairness__round" title="Position of this round within its server seed">
              {round.seedRound ? `Seed round ${round.seedRound}` : 'Round'}
            </span>
            <span className="crash-fairness__crash">{round.crashPoint}</span>
          </div>

          <div className="crash-fairness__rows">
            <ValueRow label="Round ID" value={round.roundId} copy={copy} isCopied={isCopied} />
            <ValueRow label="Round Seed" value={round.hash} copy={copy} isCopied={isCopied} />
            <ValueRow label="Game Seed" value={round.seed} copy={copy} isCopied={isCopied} />
            <ValueRow
              label="Seed Hash"
              value={round.serverSeedHash}
              copy={copy}
              isCopied={isCopied}
            />
            <ValueRow
              label="Server Seed"
              value={round.serverSeed}
              // Each round owns its seed now, so it is published when that round
              // ends — not when a shared seed rotates.
              hint="revealed when the round ends"
              copy={copy}
              isCopied={isCopied}
            />

            {/* The three client seeds this round was derived from, in slot order.
                Absent for a v1 round, which never had any — rendering empty rows
                would imply history is missing something it never had. */}
            {round.clientSeeds?.map((entry) => (
              <ValueRow
                key={entry.slot}
                label={seedLabel(entry, playerId)}
                value={entry.clientSeed}
                copy={copy}
                isCopied={isCopied}
              />
            ))}
          </div>

          {round.interrupted && (
            <div className="crash-fairness__note">
              Ended early by a restart — seed derives {round.derivedCrashPoint}.
            </div>
          )}

          {verdict && (
            <div className={`crash-fairness__verdict is-${verdict.status}`}>
              <span className="crash-fairness__verdict-icon">{STATUS_ICON[verdict.status]}</span>
              {verdict.text}
            </div>
          )}

          {/* Opens the Provably Fair screen already loaded on this round, so the
              player never has to find it by id. */}
          <button type="button" className="crash-fairness__verify" onClick={handleVerify}>
            Verify
          </button>
        </>
      )}
    </div>,
    document.body
  )
}

export default CrashRoundFairness
